import {
  InitialPageRequest,
  getInitialPageRequestTimings,
} from 'helpers/initialPagePerformance'

type TInitialPageRequestTimings = ReturnType<
  typeof getInitialPageRequestTimings
>

type TInitialPageMetric = {
  name: string
  value: number | null
}

type TNavigationTimingWithActivationStart = PerformanceNavigationTiming & {
  activationStart?: number
}

type TDocumentDeliveryAttributes = {
  protocol?: string
  cdn_pop?: string
  cdn_cache?: string
}

const CDN_CACHE_SERVER_TIMING_PREFIX = 'cdn-cache-'

export type TPaintReadyMetrics = {
  firstScreenReadyAt: number
  firstScreenReadyTimestampMs: number
  firstScreenReadyTime: number
  pageRequestTimestampMs: number
}

export type TFirstScreenReadyEventProperties = {
  page_request_timestamp_ms: number
  first_screen_ready_timestamp_ms: number
  first_screen_ready_time_ms: number
  server_response_time_ms: number | null
  client_render_time_ms: number | null
  document_request_ttfb_ms: number | null
  blocking_requests_chain_duration_ms: number | null
  config_action_duration_ms: number | null
  config_network_duration_ms: number | null
  config_request_ttfb_ms: number | null
  variant_action_duration_ms: number | null
  variant_network_duration_ms: number | null
  variant_request_ttfb_ms: number | null
  post_blocking_requests_ready_delay_ms: number | null
}

export const getNavigationEntry = (): PerformanceNavigationTiming | null => {
  const navigationEntry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined

  return navigationEntry || null
}

export const getPerformanceTimestampMs = (time: number): number =>
  Math.round((performance.timeOrigin || Date.now() - performance.now()) + time)

export const waitForNextPaint = (callback: () => void): (() => void) => {
  let firstFrameId = 0
  let secondFrameId = 0

  firstFrameId = requestAnimationFrame(() => {
    secondFrameId = requestAnimationFrame(callback)
  })

  return () => {
    cancelAnimationFrame(firstFrameId)
    cancelAnimationFrame(secondFrameId)
  }
}

export const getDocumentRequestTtfb = (
  navigationEntry: PerformanceNavigationTiming,
): number | null => {
  const { requestStart, responseStart } = navigationEntry

  if (responseStart <= requestStart) return null

  return Math.round(responseStart - requestStart)
}

export const getDocumentMetrics = (
  navigationEntry: PerformanceNavigationTiming,
): TInitialPageMetric[] => [
  {
    name: 'page_view.document_request_ttfb_ms',
    value: getDocumentRequestTtfb(navigationEntry),
  },
]

export const getDocumentDeliveryAttributes =
  (): TDocumentDeliveryAttributes => {
    const navigationEntry = getNavigationEntry()

    if (!navigationEntry) return {}

    // Safari < 16.4 doesn't support serverTiming
    const { nextHopProtocol, serverTiming = [] } = navigationEntry
    const cdnPopTiming = serverTiming.find(({ name }) => name === 'cdn-pop')
    const cdnCacheTiming = serverTiming.find(({ name }) =>
      name.startsWith(CDN_CACHE_SERVER_TIMING_PREFIX),
    )

    return {
      protocol: nextHopProtocol || undefined,
      cdn_pop: cdnPopTiming?.description || undefined,
      cdn_cache: cdnCacheTiming?.name.replace(
        CDN_CACHE_SERVER_TIMING_PREFIX,
        '',
      ),
    }
  }

export const getPaintReadyMetrics = (
  navigationEntry: PerformanceNavigationTiming,
): TPaintReadyMetrics | null => {
  if (document.visibilityState !== 'visible') return null

  const readyAt = performance.now()
  const { activationStart = 0 } =
    navigationEntry as TNavigationTimingWithActivationStart

  return {
    firstScreenReadyAt: readyAt,
    firstScreenReadyTimestampMs: getPerformanceTimestampMs(readyAt),
    firstScreenReadyTime: Math.round(Math.max(readyAt - activationStart, 0)),
    pageRequestTimestampMs: getPerformanceTimestampMs(
      navigationEntry.startTime,
    ),
  }
}

const getBlockingChainDuration = (
  requestTimings: TInitialPageRequestTimings,
): number | null => {
  const configTiming = requestTimings[InitialPageRequest.CONFIG]
  const variantTiming = requestTimings[InitialPageRequest.VARIANT]

  if (!configTiming || !variantTiming) return null

  return Math.round(
    Math.max(configTiming.completedAt, variantTiming.completedAt) -
      Math.min(configTiming.startedAt, variantTiming.startedAt),
  )
}

export const getPostBlockingRequestsReadyDelay = ({
  requestTimings,
  readyAt,
}: {
  requestTimings: TInitialPageRequestTimings
  readyAt: number
}): number | null => {
  const configTiming = requestTimings[InitialPageRequest.CONFIG]
  const variantTiming = requestTimings[InitialPageRequest.VARIANT]

  if (!configTiming || !variantTiming) return null

  const blockingRequestsCompletedAt = Math.max(
    configTiming.completedAt,
    variantTiming.completedAt,
  )

  return Math.round(Math.max(readyAt - blockingRequestsCompletedAt, 0))
}

const getServerResponseTime = ({
  documentRequestTtfb,
  requestTimings,
}: {
  documentRequestTtfb: number | null
  requestTimings: TInitialPageRequestTimings
}): number | null => {
  const configRequestTtfb =
    requestTimings[InitialPageRequest.CONFIG]?.ttfb ?? null
  const variantRequestTtfb =
    requestTimings[InitialPageRequest.VARIANT]?.ttfb ?? null

  if (
    documentRequestTtfb === null ||
    configRequestTtfb === null ||
    variantRequestTtfb === null
  ) {
    return null
  }

  return documentRequestTtfb + configRequestTtfb + variantRequestTtfb
}

const getClientRenderTime = ({
  firstScreenReadyTime,
  serverResponseTime,
}: {
  firstScreenReadyTime: number
  serverResponseTime: number | null
}): number | null => {
  if (serverResponseTime === null) return null

  const clientRenderTime = firstScreenReadyTime - serverResponseTime

  if (clientRenderTime < 0) return null

  return clientRenderTime
}

export const getFirstScreenReadyEventProperties = ({
  navigationEntry,
  paintReadyMetrics,
  requestTimings,
}: {
  navigationEntry: PerformanceNavigationTiming
  paintReadyMetrics: TPaintReadyMetrics
  requestTimings: TInitialPageRequestTimings
}): TFirstScreenReadyEventProperties => {
  const configTiming = requestTimings[InitialPageRequest.CONFIG]
  const variantTiming = requestTimings[InitialPageRequest.VARIANT]
  const documentRequestTtfb = getDocumentRequestTtfb(navigationEntry)
  const serverResponseTime = getServerResponseTime({
    documentRequestTtfb,
    requestTimings,
  })

  return {
    page_request_timestamp_ms: paintReadyMetrics.pageRequestTimestampMs,
    first_screen_ready_timestamp_ms:
      paintReadyMetrics.firstScreenReadyTimestampMs,
    first_screen_ready_time_ms: paintReadyMetrics.firstScreenReadyTime,
    server_response_time_ms: serverResponseTime,
    client_render_time_ms: getClientRenderTime({
      firstScreenReadyTime: paintReadyMetrics.firstScreenReadyTime,
      serverResponseTime,
    }),
    document_request_ttfb_ms: documentRequestTtfb,
    blocking_requests_chain_duration_ms:
      getBlockingChainDuration(requestTimings),
    config_action_duration_ms: configTiming?.actionDuration ?? null,
    config_network_duration_ms: configTiming?.networkDuration ?? null,
    config_request_ttfb_ms: configTiming?.ttfb ?? null,
    variant_action_duration_ms: variantTiming?.actionDuration ?? null,
    variant_network_duration_ms: variantTiming?.networkDuration ?? null,
    variant_request_ttfb_ms: variantTiming?.ttfb ?? null,
    post_blocking_requests_ready_delay_ms: getPostBlockingRequestsReadyDelay({
      requestTimings,
      readyAt: paintReadyMetrics.firstScreenReadyAt,
    }),
  }
}

export const getRequestMetrics = (
  requestTimings: TInitialPageRequestTimings,
): TInitialPageMetric[] => {
  const configTiming = requestTimings[InitialPageRequest.CONFIG]
  const variantTiming = requestTimings[InitialPageRequest.VARIANT]

  if (!configTiming || !variantTiming) return []

  return [
    {
      name: 'page_view.blocking_requests_chain_duration_ms',
      value: getBlockingChainDuration(requestTimings),
    },
    {
      name: 'page_view.config_action_duration_ms',
      value: configTiming.actionDuration,
    },
    {
      name: 'page_view.config_network_duration_ms',
      value: configTiming.networkDuration,
    },
    {
      name: 'page_view.config_request_ttfb_ms',
      value: configTiming.ttfb,
    },
    {
      name: 'page_view.variant_action_duration_ms',
      value: variantTiming.actionDuration,
    },
    {
      name: 'page_view.variant_network_duration_ms',
      value: variantTiming.networkDuration,
    },
    {
      name: 'page_view.variant_request_ttfb_ms',
      value: variantTiming.ttfb,
    },
  ]
}
